import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { SiteStatus } from "@anu-its/cat-grpc-pb/js/anu/cat/enums_pb";
import {
  dashboardSiteStatuses,
  siteStatusKey,
  siteStatusLabel,
  statusBadgeVariant,
} from "../constants/site-status";
import {
  Badge,
  Button,
  InlineAlert,
  MultiSelect,
  Search,
} from "@anu-its/ts-react-components";
import { SearchUsers } from "@/app/api/Users";
import { UserDetails } from "@anu-its/cat-grpc-pb/js/anu/cat_app/user_pb";
import AcademicTermDropdown from "../filters/AcademicTermDropdown";
import CourseSearch from "../filters/CourseSearch";
import { FilterType, NewFilter } from "@/app/filters/interface";

interface TableFiltersProps {
  showAcademicTerm?: boolean;
  showStatus?: boolean;
  showCourseCodeSearch?: boolean;
  showSandpitSitesForOwnerSearch?: boolean;
  onFiltersChange: (filters: NewFilter, resetCursor: boolean) => void;
}

export const TableFilters = ({
  onFiltersChange,
  showAcademicTerm = false,
  showStatus = false,
  showCourseCodeSearch = false,
  showSandpitSitesForOwnerSearch = false,
  // showAcademicOrgsFilter = false,
}: TableFiltersProps) => {
  // const { userIsOrgAdmin, userIsSuperAdmin } = useUserStore();
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [filter, setFilter] = useState<FilterType>({
    academicTermCode: [],
    courseQuery: "",
    statusFilterList: dashboardSiteStatuses,
    sandpitOwner: "",
    collegeOrgCode: "",
    academicOrgCode: "",
    academicProgram: undefined,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["SearchUsers", userQuery],
    queryFn: () => SearchUsers(userQuery),
    enabled: !!userQuery,
  });

  // Main filter function. Todo: all other logic will be moved to own components
  const updateFilter = (updatedFilter: FilterType, resetCursor: boolean) => {
    setFilter({ ...filter, ...updatedFilter });
    onFiltersChange({ ...filter, ...updatedFilter }, resetCursor);
  };

  const resetFilter = () => {
    setFilter({
      ...filter,
      courseQuery: "",
      statusFilterList: dashboardSiteStatuses,
      sandpitOwner: "",
      collegeOrgCode: "",
      academicOrgCode: "",
    });

    onFiltersChange(
      {
        ...filter,
        courseQuery: "",
        statusFilterList: dashboardSiteStatuses,
        sandpitOwner: "",
        collegeOrgCode: "",
        academicOrgCode: "",
      },
      true,
    );

    setSelectedUser(null);
  };

  const handleUserSelect = (selectedUser: UserDetails | null): void => {
    setSelectedUser(selectedUser);
    const userUid = selectedUser?.userId.toString() || "";
    setFilter({ ...filter, sandpitOwner: userUid });
    onFiltersChange({ ...filter, sandpitOwner: userUid }, true);
  };

  const onStatusSelect = (statuses: string[]) => {
    const updatedStatuses =
      statuses.length > 0
        ? statuses
            .map(siteStatusKey)
            .filter((status): status is SiteStatus => status !== undefined)
        : dashboardSiteStatuses;

    setFilter({
      ...filter,
      statusFilterList: updatedStatuses,
    });
    onFiltersChange(
      {
        ...filter,
        statusFilterList: updatedStatuses,
      },
      true,
    );
  };

  return (
    <>
      <div className="flex items-end justify-end flex-wrap gap-6 pb-5">
        {/*TODO: future enhancement add support for non-admin search */}
        {showCourseCodeSearch && (
          <CourseSearch
            setErrorMessage={setErrorMessage}
            value={filter.courseQuery ? filter.courseQuery.trim() : ""}
            updateFilter={updateFilter}
          />
        )}

        {showAcademicTerm && (
          <AcademicTermDropdown
            updateFilter={updateFilter}
            multiSelect={true}
          />
        )}

        {/*{showAcademicOrgsFilter && (userIsOrgAdmin || userIsSuperAdmin) && (*/}
        {/*  <div className="xl:min-w-[55rem] flex-grow">*/}
        {/*    <AcademicOrgsDropdown*/}
        {/*      collegeOrgCode={filter.collegeOrgCode}*/}
        {/*      academicOrgCode={filter.academicOrgCode}*/}
        {/*      updateFilter={updateFilter}*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*)}*/}

        {/* TODO: move to own component */}
        {showSandpitSitesForOwnerSearch && (
          <div className="relative flex grow items-center rounded-md pr-2 w-64">
            <Search
              accessibilityLabel="Search User"
              id="owner-search"
              onSearch={(query) => setUserQuery(query)}
              data={data && !(data instanceof Error) ? data.userDetails : []}
              isLoading={isLoading}
              isError={error}
              searchPlaceholder={
                selectedUser
                  ? `Showing results for: ${selectedUser.name?.display} - ${selectedUser.userId}`
                  : "Search User"
              }
              minQueryLength={2}
              renderItem={(selectedUser) => (
                <div>
                  <span>{selectedUser.userId}</span> -
                  <span>{selectedUser.name.display}</span>
                </div>
              )}
              onItemSelect={handleUserSelect}
            />
          </div>
        )}

        {/* TODO: move to own component */}
        {showStatus && (
          <MultiSelect
            selectPlaceholderText="Status"
            options={Object.values(dashboardSiteStatuses).map((status) => ({
              value: siteStatusLabel(status),
              label: (
                <Badge size="sm" variant={statusBadgeVariant[status]}>
                  {siteStatusLabel(status)}
                </Badge>
              ),
            }))}
            selected={
              filter.statusFilterList &&
              filter.statusFilterList.map((status) =>
                siteStatusLabel(status as SiteStatus),
              )
            }
            onSelectionChange={onStatusSelect}
          />
        )}

        <Button
          onClick={resetFilter}
          classNames="!py-1 !px-2 rounded-full !border-[1px]"
          variant="tertiary"
          size="small"
        >
          Reset
        </Button>
      </div>
      {errorMessage && (
        <div className="pb-5">
          <InlineAlert variant="negative">{errorMessage}</InlineAlert>
        </div>
      )}
    </>
  );
};
